/**
 * Route admin `/tutor/admin/animations/sons` — « Sons des animations ».
 *
 * Bibliothèque audio des animations JRAF (migration 0669) : musique
 * d'ambiance (très basse, en boucle) et jingles du début et de la fin. Le son
 * « par défaut » de chaque type est celui que jouent toutes les animations.
 * Écouter, téléverser, choisir, régler le volume, supprimer ; importer une
 * musique de la médiathèque YouTube ; installer les jingles JeRaisonne livrés
 * avec l'app (joués en attendant, quand la base n'en a pas).
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, Check, Download, Music, Trash2, Upload } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import {
  animationAudioUrl,
  AUDIO_KIND_LABEL,
  BUNDLED_JINGLES,
  bundledJingleUrl,
  musicLabel,
  useAddAnimationAudio,
  useAnimationAudioList,
  useDeleteAnimationAudio,
  useImportYoutubeMusic,
  useInstallBundledJingle,
  useSetAnimationAudioDefault,
  useUpdateAnimationAudio,
  useYoutubeMusic,
  type AnimationAudioKind,
  type AnimationAudioRow,
  type YoutubeMusic,
} from '@/lib/animations/audio';

const KINDS: readonly AnimationAudioKind[] = ['music', 'jingle_intro', 'jingle_outro'];

const KIND_HELP: Record<AnimationAudioKind, string> = {
  music:
    'En boucle sous toute l’animation, très bas (12\u202f% conseillé) — elle s’efface sous les jingles et s’éteint au générique.',
  jingle_intro: 'Sur « JeRaisonne présente », au départ de la boussole.',
  jingle_outro: 'Sur le générique, avec le message de fin.',
};

function seconds(ms: number | null): string {
  if (ms === null) return '';
  const s = Math.round(ms / 1000);
  return s >= 60 ? `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, '0')}` : `${s} s`;
}

function size(bytes: number | null): string {
  if (bytes === null) return '';
  return bytes > 1e6
    ? `${(bytes / 1e6).toFixed(1).replace('.', ',')} Mo`
    : `${Math.round(bytes / 1e3)} Ko`;
}

const btn =
  'inline-flex min-h-11 items-center gap-1.5 rounded-xl px-3 text-sm font-semibold transition-colors active:scale-[0.98] disabled:opacity-50';

/** Volume : appliqué en base quand on lâche le curseur. */
function VolumeControl({ row }: { row: AnimationAudioRow }) {
  const update = useUpdateAnimationAudio();
  const [value, setValue] = useState(Math.round(row.gain * 100));
  useEffect(() => setValue(Math.round(row.gain * 100)), [row.gain]);
  const commit = () => {
    if (value !== Math.round(row.gain * 100)) update.mutate({ id: row.id, gain: value / 100 });
  };
  return (
    <label className="text-ink-600 flex min-h-11 items-center gap-2 text-[13px]">
      <span className="w-14 shrink-0">Volume</span>
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        onPointerUp={commit}
        onKeyUp={commit}
        onBlur={commit}
        aria-valuetext={`${value} %`}
        className="accent-primary-500 h-11 w-full max-w-56"
      />
      <span className="text-ink-800 w-12 whitespace-nowrap text-right font-semibold tabular-nums">
        {`${value}\u202f%`}
      </span>
    </label>
  );
}

function AudioRow({ row }: { row: AnimationAudioRow }) {
  const setDefault = useSetAnimationAudioDefault();
  const remove = useDeleteAnimationAudio();
  const [confirm, setConfirm] = useState(false);
  const meta = [seconds(row.duration_ms), size(row.bytes), row.license ?? '']
    .filter(Boolean)
    .join(' · ');
  return (
    <li
      className={cn(
        'rounded-2xl border bg-white p-3',
        row.is_default ? 'border-primary-300 ring-primary-100 ring-2' : 'border-ink-200',
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-ink-900 break-words text-[15px] font-semibold">{row.label}</p>
          {meta && <p className="text-ink-500 text-[13px]">{meta}</p>}
          {row.source_url && (
            <a
              href={row.source_url}
              target="_blank"
              rel="noreferrer"
              className="text-primary-600 text-[13px] hover:underline"
            >
              Source
            </a>
          )}
        </div>
        {row.is_default ? (
          <span className="bg-primary-50 text-primary-700 inline-flex min-h-8 items-center gap-1 rounded-full px-2.5 text-xs font-bold">
            <Check className="h-3.5 w-3.5" aria-hidden /> Par défaut
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setDefault.mutate(row.id)}
            disabled={setDefault.isPending}
            className={cn(btn, 'border-ink-200 text-ink-700 hover:bg-ink-50 border')}
          >
            Utiliser par défaut
          </button>
        )}
      </div>
      <audio
        controls
        preload="none"
        src={animationAudioUrl(row.bucket_path)}
        className="mt-2 h-10 w-full"
        aria-label={`Écouter « ${row.label} »`}
      />
      <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
        <VolumeControl row={row} />
        {confirm ? (
          <span className="flex items-center gap-2 text-[13px]">
            <span className="text-ink-700">Supprimer ce son ?</span>
            <button
              type="button"
              onClick={() => remove.mutate(row)}
              disabled={remove.isPending}
              className={cn(btn, 'bg-error-600 hover:bg-error-500 text-white')}
            >
              Supprimer
            </button>
            <button
              type="button"
              onClick={() => setConfirm(false)}
              className={cn(btn, 'text-ink-600 hover:bg-ink-50')}
            >
              Annuler
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setConfirm(true)}
            className={cn(btn, 'text-ink-500 hover:bg-ink-50 hover:text-error-600')}
            aria-label={`Supprimer « ${row.label} »`}
          >
            <Trash2 className="h-4 w-4" aria-hidden /> Supprimer
          </button>
        )}
      </div>
    </li>
  );
}

function UploadButton({ kind, hasDefault }: { kind: AnimationAudioKind; hasDefault: boolean }) {
  const add = useAddAnimationAudio();
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        accept="audio/mpeg,audio/mp4,audio/aac,audio/ogg,audio/webm,.mp3,.m4a,.aac,.ogg,.webm"
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          add.mutate({
            kind,
            blob: file,
            fileName: file.name,
            label:
              kind === 'music' ? musicLabel(file.name) : file.name.replace(/\.[a-z0-9]+$/i, ''),
            makeDefault: !hasDefault,
          });
        }}
      />
      <button
        type="button"
        onClick={() => input.current?.click()}
        disabled={add.isPending}
        className={cn(btn, 'bg-primary-500 hover:bg-primary-600 text-white')}
      >
        <Upload className="h-4 w-4" aria-hidden />
        {add.isPending ? 'Envoi…' : 'Ajouter un fichier'}
      </button>
      {add.error && (
        <p role="alert" className="text-error-600 w-full text-[13px]">
          {add.error.message}
        </p>
      )}
    </>
  );
}

function YoutubeImport({
  imported,
  hasDefault,
}: {
  imported: readonly AnimationAudioRow[];
  hasDefault: boolean;
}) {
  const list = useYoutubeMusic();
  const importer = useImportYoutubeMusic();
  const [busy, setBusy] = useState<string | null>(null);
  const already = (m: YoutubeMusic) =>
    imported.some(
      (r) =>
        (m.music_source_url && r.source_url === m.music_source_url) ||
        r.label === musicLabel(m.label),
    );
  const todo = (list.data ?? []).filter((m) => !already(m));
  if (list.isLoading || todo.length === 0) return null;
  return (
    <div className="border-ink-200 mt-3 rounded-2xl border border-dashed p-3">
      <p className="text-ink-700 text-[14px] font-semibold">Médiathèque YouTube</p>
      <p className="text-ink-500 mb-2 text-[13px]">
        Les musiques déjà utilisées pour les vidéos YouTube.
      </p>
      <ul className="space-y-2">
        {todo.map((m) => (
          <li key={m.id} className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-ink-800 min-w-0 break-words text-[14px]">
              {musicLabel(m.label)}
            </span>
            <button
              type="button"
              disabled={importer.isPending}
              onClick={() => {
                setBusy(m.id);
                importer.mutate(
                  { music: m, makeDefault: !hasDefault },
                  { onSettled: () => setBusy(null) },
                );
              }}
              className={cn(btn, 'border-ink-200 text-ink-700 hover:bg-ink-50 border')}
            >
              <Download className="h-4 w-4" aria-hidden />
              {busy === m.id ? 'Import…' : hasDefault ? 'Importer' : 'Importer et utiliser'}
            </button>
          </li>
        ))}
      </ul>
      {importer.error && (
        <p role="alert" className="text-error-600 mt-2 text-[13px]">
          {importer.error.message}
        </p>
      )}
    </div>
  );
}

function BundledJingle({
  kind,
  hasDefault,
}: {
  kind: 'jingle_intro' | 'jingle_outro';
  hasDefault: boolean;
}) {
  const install = useInstallBundledJingle();
  return (
    <div className="border-ink-200 mt-3 rounded-2xl border border-dashed p-3">
      <p className="text-ink-700 text-[14px] font-semibold">{BUNDLED_JINGLES[kind].label}</p>
      <p className="text-ink-500 text-[13px]">
        Livré avec l’app, libre de droits
        {hasDefault ? '' : ' — c’est lui qu’on entend tant qu’aucun jingle n’est choisi ici'}.
      </p>
      <audio
        controls
        preload="none"
        src={bundledJingleUrl(kind)}
        className="mt-2 h-10 w-full"
        aria-label={`Écouter « ${BUNDLED_JINGLES[kind].label} »`}
      />
      <button
        type="button"
        onClick={() => install.mutate({ kind, makeDefault: !hasDefault })}
        disabled={install.isPending}
        className={cn(btn, 'border-ink-200 text-ink-700 hover:bg-ink-50 mt-2 border')}
      >
        <Download className="h-4 w-4" aria-hidden />
        {install.isPending ? 'Installation…' : 'L’ajouter à la bibliothèque'}
      </button>
      {install.error && (
        <p role="alert" className="text-error-600 mt-2 text-[13px]">
          {install.error.message}
        </p>
      )}
    </div>
  );
}

export function TutorAdminAnimationAudioPage() {
  const list = useAnimationAudioList();
  const rows = list.data ?? [];
  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <Link
        to="/tutor/admin/animations"
        className="text-primary-600 mb-2 inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden /> Animations SKILLCARDS
      </Link>
      <h1 className="font-display text-ink-900 flex items-center gap-2 text-[22px] font-extrabold">
        <Music className="text-primary-500 h-5 w-5" aria-hidden /> Sons des animations
      </h1>
      <p className="text-ink-500 mt-1 text-[14px]">
        Joués par toutes les animations, dans l’app et dans les vidéos. Le son « par défaut » de
        chaque type est celui qu’on entend ; « Couper le son » dans le lecteur les coupe aussi.
      </p>

      {list.isLoading && <p className="text-ink-500 mt-4 text-sm">Chargement…</p>}
      {list.error && (
        <p role="alert" className="text-error-600 mt-4 text-sm">
          Impossible de charger la bibliothèque.
        </p>
      )}

      {list.data &&
        KINDS.map((kind) => {
          const mine = rows.filter((r) => r.kind === kind);
          const hasDefault = mine.some((r) => r.is_default);
          const bundledInstalled = mine.some((r) => r.bucket_path.startsWith('defaults/'));
          return (
            <section key={kind} aria-labelledby={`sons-${kind}`} className="mt-6">
              <div className="flex flex-wrap items-end justify-between gap-2">
                <div>
                  <h2 id={`sons-${kind}`} className="font-display text-ink-900 text-base font-bold">
                    {AUDIO_KIND_LABEL[kind]}
                  </h2>
                  <p className="text-ink-500 text-[13px]">{KIND_HELP[kind]}</p>
                </div>
                <UploadButton kind={kind} hasDefault={hasDefault} />
              </div>
              {mine.length === 0 && kind === 'music' && (
                <p className="text-ink-500 mt-2 text-[13px]">
                  Aucune musique : les animations n’en jouent pas.
                </p>
              )}
              <ul className="mt-3 space-y-3">
                {mine.map((r) => (
                  <AudioRow key={r.id} row={r} />
                ))}
              </ul>
              {kind === 'music' && <YoutubeImport imported={mine} hasDefault={hasDefault} />}
              {kind !== 'music' && !bundledInstalled && (
                <BundledJingle kind={kind} hasDefault={hasDefault} />
              )}
            </section>
          );
        })}
    </div>
  );
}
